'use client';

import { MouseEvent, useState } from 'react';
import { endOfWeek, startOfDay, startOfWeek } from 'date-fns';

import { Dialog } from '@/app/components/Dialog';
import { FiX, FiWatch, FiSave, FiPlus } from 'react-icons/fi';
import { cn } from '@/utils/uiUtils';
import { TimePeriod, WorkoutStatus } from '@/utils/prismaEnums';
import { WorkoutEditor } from '@/app/workouts/WorkoutEditor';
import type { WorkoutWithRelations } from '@/utils/types';
import { toast } from 'react-toastify';
import { trpc } from '@/app/api/trpc/client';
import { useAnalysisStore } from '@/app/stores/analysisStore';
import { useCalendar } from '@/app/calendar/utils/useCalendar';
import { useCalendarState } from '@/app/calendar/utils/useCalendarState';
import { useWorkoutStore } from '@/app/stores/workoutStore';
import { ActivityAnalysisActions } from './ActivityAnalysisActions';

type Props = {
  isModalOpen: boolean;
  close: () => void;
};

export const EditWorkoutModal = ({ isModalOpen, close }: Props) => {
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [analysisTriggerState, setAnalysisTriggerState] = useState<{
    canTriggerAnalysis: boolean;
    triggerAnalysis: () => void;
    isTriggeringAnalysis: boolean;
  } | null>(null);
  const [garminSyncState, setGarminSyncState] = useState<{
    canSync: boolean;
    handleGarminAction: () => void;
    isGarminActionLoading: boolean;
    buttonTitle: string;
  } | null>(null);
  const { workout, setWorkout } = useCalendarState();
  const hasAnalysis = Boolean(workout?.analysis);
  const isLocked = Boolean(workout?.isUserEdited);
  const statusKey = workout?.status ?? WorkoutStatus.PENDING;
  const canClickStatus =
    statusKey === WorkoutStatus.PENDING &&
    Boolean(analysisTriggerState?.canTriggerAnalysis) &&
    !analysisTriggerState?.isTriggeringAnalysis;

  const handleToggleLock = () => {
    if (!workout) return;
    setWorkout({ ...workout, isUserEdited: !workout.isUserEdited });
  };

  const editorModalClassName = hasAnalysis
    ? 'w-[calc(100vw-1rem)] max-w-[calc(100vw-1rem)] h-[calc(100vh-2rem)] max-h-[calc(100vh-2rem)] xs:w-[calc(100vw-2rem)] xs:max-w-[calc(100vw-2rem)] xs:h-[calc(100vh-3rem)] xs:max-h-[calc(100vh-3rem)] lg:w-[75vw] lg:max-w-[75vw] lg:h-[90vh]'
    : 'w-[calc(100vw-1rem)] max-w-[calc(100vw-1rem)] max-h-[calc(100vh-2rem)] xs:w-[calc(100vw-2rem)] xs:max-w-[calc(100vw-2rem)] xs:max-h-[calc(100vh-3rem)] lg:w-[min(55vw,760px)] lg:max-w-[min(55vw,760px)] lg:max-h-[90vh]';

  return (
    <Dialog
      show={isModalOpen}
      onClose={close}
      dismissible={true}
      className={editorModalClassName}
    >
      <Dialog.Header className='flex items-center justify-between gap-4 relative'>
        <div className='flex items-center gap-2'>
          <h3 className='h3 my-0'>Editing workout:</h3>
          <div className='text-gray-500 dark:text-gray-400'>
            {workout?.name}
          </div>
        </div>
        <div className='flex items-center gap-1 ml-auto'>
          <ActivityAnalysisActions
            status={workout?.status}
            onClick={
              canClickStatus
                ? () => analysisTriggerState?.triggerAnalysis()
                : undefined
            }
            disabled={!canClickStatus}
            title={
              canClickStatus
                ? 'Trigger analysis for this pending workout'
                : undefined
            }
            labelOverride={
              analysisTriggerState?.isTriggeringAnalysis
                ? 'Analyzing...'
                : undefined
            }
          />
          <button
            onClick={close}
            className='p-2 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-full transition-colors text-gray-500 dark:text-gray-400'
            aria-label='Close'
          >
            <FiX size={20} />
          </button>
        </div>
      </Dialog.Header>
      <Dialog.Body className='flex min-h-0 flex-1 overflow-y-auto overflow-x-hidden p-0'>
        <div className='h-full min-h-0 w-full'>
          <WorkoutEditor
            onSave={() => {
              close();
            }}
            onDelete={() => {
              setIsDeleteModalOpen(true);
            }}
            onClose={close}
            hideFooter={true}
            onAnalysisReady={() => {
              window.requestAnimationFrame(() => {
                window.dispatchEvent(new Event('resize'));
              });
            }}
            onAnalysisTriggerStateChange={setAnalysisTriggerState}
            onGarminSyncStateChange={setGarminSyncState}
          />
        </div>
      </Dialog.Body>
      <Dialog.Footer className='shrink-0 border-t border-gray-100 bg-white px-3 py-1.5 dark:border-gray-800 dark:bg-slate-900'>
        <div className='flex items-center justify-between gap-4 w-full'>
          <div className='flex items-center gap-2'>
            <button
              type='button'
              className={cn(
                'inline-flex items-center gap-1 border border-current rounded-lg py-1 px-2 text-sm mr-3 transition-opacity',
                garminSyncState?.canSync
                  ? 'text-orange-500 hover:text-orange-400'
                  : 'text-gray-400 cursor-not-allowed opacity-50',
                garminSyncState?.isGarminActionLoading &&
                  'cursor-wait opacity-80'
              )}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                garminSyncState?.handleGarminAction?.();
              }}
              disabled={
                !garminSyncState?.canSync ||
                garminSyncState?.isGarminActionLoading
              }
              title={garminSyncState?.buttonTitle ?? 'Sync Garmin'}
            >
              <FiWatch
                size={16}
                className={cn(
                  garminSyncState?.isGarminActionLoading && 'animate-spin'
                )}
              />
              <span>{garminSyncState?.buttonTitle ?? 'Sync Garmin'}</span>
            </button>
          </div>

          <div className='flex items-center gap-3'>
            {true && (
              <button
                type='button'
                className='btn btn-danger'
                onClick={() => setIsDeleteModalOpen(true)}
              >
                Delete
              </button>
            )}
            <button type='button' className='btn btn-secondary' onClick={close}>
              Cancel
            </button>
            <button
              type='submit'
              form='workout-editor-form'
              className='btn btn-primary'
            >
              {workout?.id ? (
                <>
                  <FiSave size={14} />
                  Update
                </>
              ) : (
                <>
                  <FiPlus size={14} />
                  Create
                </>
              )}
            </button>
          </div>
        </div>
      </Dialog.Footer>
      <ConfirmDeleteModal
        workout={workout}
        isModalOpen={isDeleteModalOpen}
        close={() => {
          setIsDeleteModalOpen(false);
          close();
        }}
      />
    </Dialog>
  );
};

export const ConfirmDeleteModal = ({
  workout,
  isModalOpen,
  close,
}: {
  workout: WorkoutWithRelations | null;
  isModalOpen: boolean;
  close: () => void;
}) => {
  const { workouts, setWorkouts } = useCalendarState();
  const { removeWorkout } = useWorkoutStore();
  const { removeAnalysis } = useAnalysisStore();
  const { loadWeekData } = useCalendar();
  const utils = trpc.useUtils();

  const { mutate: deleteWorkout } = trpc.workouts.deleteById.useMutation({
    onSuccess: async () => {
      toast.success('Workout deleted', {
        autoClose: 1000,
      });

      // Remove from Zustand store for immediate UI update
      if (workout?.id) {
        // Ensure ID is converted to string to match store key format
        removeWorkout(workout.id.toString());

        // Also remove the associated analysis if it exists
        if (workout.analysis?.id) {
          removeAnalysis(workout.analysis.id.toString());
        }
      }

      // Also update calendar state for other views
      const updatedWorkouts = workouts.filter(
        (w: WorkoutWithRelations) => w.id !== workout?.id
      );
      setWorkouts(updatedWorkouts);

      // Invalidate cache for future loads
      utils.workouts.getMany.invalidate();
      utils.analysis.getMany.invalidate();

      // Reload the affected date's week without showing loading indicators
      if (workout?.date) {
        const workoutDate =
          workout.date instanceof Date ? workout.date : new Date(workout.date);
        const weekStart = startOfWeek(workoutDate, { weekStartsOn: 1 });
        const weekEnd = endOfWeek(workoutDate, { weekStartsOn: 1 });
        await loadWeekData({ start: weekStart, end: weekEnd }, true);

        // Invalidate and refetch timely analysis queries for the affected day and week
        const normalizedDate = startOfDay(workoutDate);
        const normalizedWeekStart = startOfDay(weekStart);
        await Promise.all([
          utils.analysis.getTimelyAnalysis.invalidate({
            period: TimePeriod.DAILY,
            startDate: normalizedDate,
          }),
          utils.analysis.getTimelyAnalysis.invalidate({
            period: TimePeriod.WEEKLY,
            startDate: normalizedWeekStart,
          }),
        ]);
        // Explicitly fetch to ensure UI updates
        await Promise.all([
          utils.analysis.getTimelyAnalysis.fetch({
            period: TimePeriod.DAILY,
            startDate: normalizedDate,
          }),
          utils.analysis.getTimelyAnalysis.fetch({
            period: TimePeriod.WEEKLY,
            startDate: normalizedWeekStart,
          }),
        ]);
      }
    },
    onError: (error) => {
      toast.error('Failed to delete workout: ' + error);
    },
  });

  // Function to handle delete confirmation
  const handleDeleteConfirmation = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (workout) {
      deleteWorkout({ id: workout.id });
      close();
    }
  };

  if (!workout) {
    return null;
  }

  return (
    <Dialog show={isModalOpen} onClose={close} dismissible={true}>
      <Dialog.Header>Confirm deleting</Dialog.Header>
      <Dialog.Body>
        <p>
          Are you sure you want to delete this workout:
          <br />
          <i>{workout.name}</i>?
        </p>
      </Dialog.Body>
      <Dialog.Footer>
        <button onClick={close} className='btn'>
          Cancel
        </button>
        <button onClick={handleDeleteConfirmation} className='btn btn-danger'>
          Confirm
        </button>
      </Dialog.Footer>
    </Dialog>
  );
};
